import React, { useEffect, useState, memo } from "react";
import { Box, Typography } from "@mui/material";
import AddCircle from "../assets/icons/add-circle.svg";
import CloseCircle from "../assets/icons/close-circle.svg";
import {
  blackColor,
  grayColor,
  lightBlackColor,
  violetColor,
  whiteColor,
} from "../constants/colors";
import TextInput from "../components/TextInput";
import RangeSlider from "../components/RangeSlider";
import SmallTextInput from "../components/SmallTextInput";

const Formats = ({
  formats,
  setSelectedResolutions,
  setSelectedCompositions,
  currentTemplate,
  handleModificationChange,
  modifications,
  setModifications,
}) => {
  const [isOpenFormats, setIsOpenFormats] = useState(false);
  const [isHaveMergeVideos, setIsHaveMergeVideos] = useState(false);

  const getIsHaveMergeVideos = () => {
    let haveMergedVideos = false;
    currentTemplate?.layersInfo.forEach((layer) => {
      if (haveMergedVideos) return;
      Object.entries(layer).forEach(([sourceKey, objectsArray]) => {
        for (const obj of objectsArray) {
          if (obj.value.includes("videoReplace_2")) {
            haveMergedVideos = true;
            return;
          }
          haveMergedVideos = false;
        }
      });
    });
    setIsHaveMergeVideos(haveMergedVideos);
  };

  useEffect(() => {
    getIsHaveMergeVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate?.layersInfo]);

  const handleCompositionSelection = (composition, isChecked) => {
    setSelectedCompositions((prev) => {
      if (isChecked) {
        return [...prev, composition];
      } else {
        return prev.filter((item) => item !== composition);
      }
    });

    const resolution = composition.match(/\d+x\d+/)[0];
    setSelectedResolutions((prev) => ({ ...prev, [resolution]: isChecked }));
  };

  const updateLayer = (
    resolutionKey,
    layerType,
    layerValue,
    startCutTimecode,
    endCutTimecode
  ) => {
    if (isHaveMergeVideos) {
      if (layerValue.includes("videoReplace_1")) {
        const secondVideo = modifications.sourceFootage800x1000.layers.find(
          (layer) => layer.value.includes("videoReplace_2")
        );
        if (secondVideo) {
          const finalLength =
            secondVideo.endCutTimecode -
            secondVideo.startCutTimecode +
            endCutTimecode -
            startCutTimecode;
          if (finalLength > 59) {
            return;
          }
        }
      }
      if (layerValue.includes("videoReplace_2")) {
        const firstVideo = modifications.sourceFootage800x1000.layers.find(
          (layer) => layer.value.includes("videoReplace_1")
        );
        if (firstVideo) {
          const finalLength =
            firstVideo.endCutTimecode -
            firstVideo.startCutTimecode +
            endCutTimecode -
            startCutTimecode;
          if (finalLength > 59) {
            return;
          }
        }
      }
    }

    setModifications((prevModifications) => {
      const newModifications = { ...prevModifications };

      if (!newModifications[resolutionKey]) {
        newModifications[resolutionKey] = {
          layers: [
            {
              type: layerType,
              value: layerValue,
              startCutTimecode,
              endCutTimecode,
            },
          ],
        };
      } else {
        const layers = newModifications[resolutionKey].layers;
        const layerIndex = layers.findIndex(
          (layer) => layer.type === layerType && layer.value === layerValue
        );

        if (layerIndex === -1) {
          layers.push({
            type: layerType,
            value: layerValue,
            startCutTimecode,
            endCutTimecode,
          });
        } else {
          layers[layerIndex] = {
            ...layers[layerIndex],
            ...(startCutTimecode !== undefined && { startCutTimecode }),
            ...(endCutTimecode !== undefined && { endCutTimecode }),
          };
        }
      }

      return newModifications;
    });
  };

  const handleChangeVideo1Cut = (event, newValue: string[]) => {
    const rightValue = newValue[1];
    const leftValue = newValue[0];

    updateLayer(
      "sourceFootage1920x1080",
      "video",
      "videoReplace_1_1920x1080_59s.mp4",
      +leftValue,
      +rightValue
    );
    updateLayer(
      "sourceFootage1080x1920",
      "video",
      "videoReplace_1_1080x1920_59s.mp4",
      +leftValue,
      +rightValue
    );
    updateLayer(
      "sourceFootage800x1000",
      "video",
      "videoReplace_1_800x1000_59s.mp4",
      +leftValue,
      +rightValue
    );
  };

  const handleChangeVideo2Cut = (event, newValue: string[]) => {
    const rightValue = newValue[1];
    const leftValue = newValue[0];

    updateLayer(
      "sourceFootage1920x1080",
      "video",
      "videoReplace_2_1920x1080_59s.mp4",
      +leftValue,
      +rightValue
    );
    updateLayer(
      "sourceFootage1080x1920",
      "video",
      "videoReplace_2_1080x1920_59s.mp4",
      +leftValue,
      +rightValue
    );
    updateLayer(
      "sourceFootage800x1000",
      "video",
      "videoReplace_2_800x1000_59s.mp4",
      +leftValue,
      +rightValue
    );
  };

  const getFormatName = (val) =>
    ({
      "1080x1920": "NNN_C000_V1_WW_VID_1080x1920_59s",
      "1920x1080": "NNN_C000_V1_WW_VID_1920x1080_59s",
      "800x1000": "NNN_C000_V1_WW_VID_800x1000_59s",
    }[val]);

  const getFormatComponent = (val, isActive) =>
    ({
      "1080x1920": (
        <Box
          sx={{
            transition: "all 0.5s ease",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "75px",
            height: "135px",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: violetColor,
            background: isActive
              ? "linear-gradient(to bottom, rgba(255, 145, 250, 1),rgba(110, 90, 180, 1) )"
              : grayColor,
            "&:hover": {
              background: isActive
                ? "linear-gradient(to bottom, rgba(255, 145, 250, 1),rgba(110, 90, 180, 1) )"
                : lightBlackColor,
            },
          }}
        >
          <Typography
            sx={{
              color: whiteColor,
              fontWeight: "500",
              fontSize: "18px",
            }}
          >
            9x16
          </Typography>
        </Box>
      ),
      "1920x1080": (
        <Box
          sx={{
            transition: "all 0.5s ease",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "75px",
            width: "135px",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: violetColor,
            background: isActive
              ? "linear-gradient(to bottom, rgba(255, 145, 250, 1),rgba(110, 90, 180, 1) )"
              : grayColor,
            "&:hover": {
              background: isActive
                ? "linear-gradient(to bottom, rgba(255, 145, 250, 1),rgba(110, 90, 180, 1) )"
                : lightBlackColor,
            },
          }}
        >
          <Typography
            sx={{
              color: whiteColor,
              fontWeight: "500",
              fontSize: "18px",
            }}
          >
            16x9
          </Typography>
        </Box>
      ),
      "800x1000": (
        <Box
          sx={{
            transition: "all 0.5s ease",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "125px",
            width: "100px",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: violetColor,
            background: isActive
              ? "linear-gradient(to bottom, rgba(255, 145, 250, 1),rgba(110, 90, 180, 1) )"
              : grayColor,
            "&:hover": {
              background: isActive
                ? "linear-gradient(to bottom, rgba(255, 145, 250, 1),rgba(110, 90, 180, 1) )"
                : lightBlackColor,
            },
          }}
        >
          <Typography
            sx={{
              color: whiteColor,
              fontWeight: "500",
              fontSize: "18px",
            }}
          >
            4x5
          </Typography>
        </Box>
      ),
    }[val]);

  return (
    <Box
      sx={{
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#000000",
        paddingX: "16px",
        paddingY: "14px",
        backgroundColor: "#ffffff",
        width: "100%",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        onClick={() => setIsOpenFormats((prev) => !prev)}
      >
        <Typography
          sx={{
            color: "rgba(110,90,180, 1)",
            fontWeight: "500",
            fontSize: "36px",
            marginRight: "12px",
          }}
        >
          FORMAT
        </Typography>
        {isOpenFormats ? (
          <img
            src={CloseCircle}
            alt="close-circle"
            style={{ width: "35px", height: "35px" }}
          />
        ) : (
          <img
            src={AddCircle}
            alt="add-circle"
            style={{ width: "35px", height: "35px" }}
          />
        )}
      </Box>
      <Box
        sx={{
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          marginBottom: isOpenFormats ? "10px" : "0px",
          marginTop: isOpenFormats ? "10px" : "0px",
          transition: "all 0.5s ease",
          height: isOpenFormats
            ? isHaveMergeVideos
              ? "500px"
              : "318px"
            : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-around",
          }}
        >
          {Object.keys(formats).map((format) => {
            return (
              <Box
                key={format}
                sx={{ position: "relative", cursor: "pointer" }}
                onClick={() =>
                  handleCompositionSelection(
                    getFormatName(format),
                    !formats[format]
                  )
                }
              >
                {getFormatComponent(format, formats[format])}
              </Box>
            );
          })}
        </Box>
        <Box>
          <Typography
            sx={{ fontWeight: "500", fontSize: "20px", color: lightBlackColor }}
          >
            SRC1 ({isHaveMergeVideos ? "0-30s" : "0-59s"})
          </Typography>
          <Box
            sx={{
              width: "100%",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: blackColor,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "20px",
              }}
            >
              <TextInput
                label={"9x16 SRC1"}
                placeholder={"*.mp4 link"}
                inputStyle={{ width: "105px" }}
                disabled={!formats["1080x1920"]}
                value={
                  modifications?.["sourceFootage1080x1920"]?.layers?.find(
                    (layer) =>
                      layer.type === "video" &&
                      layer.value === "videoReplace_1_1080x1920_59s.mp4"
                  )?.newValue
                }
                onChange={(val) =>
                  handleModificationChange(
                    val,
                    "sourceFootage1080x1920",
                    "newValue",
                    "video",
                    "videoReplace_1_1080x1920_59s.mp4"
                  )
                }
              />
              <TextInput
                label={"16x9 SRC1"}
                placeholder={"*.mp4 link"}
                inputStyle={{ width: "105px" }}
                disabled={!formats["1920x1080"]}
                value={
                  modifications?.["sourceFootage1920x1080"]?.layers?.find(
                    (layer) =>
                      layer.type === "video" &&
                      layer.value === "videoReplace_1_1920x1080_59s.mp4"
                  )?.newValue
                }
                onChange={(val) => {
                  handleModificationChange(
                    val,
                    "sourceFootage1920x1080",
                    "newValue",
                    "video",
                    "videoReplace_1_1920x1080_59s.mp4"
                  );
                }}
              />
              <TextInput
                label={"4x5 SRC1"}
                placeholder={"*.mp4 link"}
                inputStyle={{ width: "105px" }}
                disabled={!formats["800x1000"]}
                value={
                  modifications?.["sourceFootage800x1000"]?.layers?.find(
                    (layer) =>
                      layer.type === "video" &&
                      layer.value === "videoReplace_1_800x1000_59s.mp4"
                  )?.newValue
                }
                onChange={(val) => {
                  handleModificationChange(
                    val,
                    "sourceFootage800x1000",
                    "newValue",
                    "video",
                    "videoReplace_1_800x1000_59s.mp4"
                  );
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  color: lightBlackColor,
                  fontWeight: "500",
                  marginRight: "20px",
                }}
              >
                CUT
              </Typography>
              <Box
                sx={{ width: "170px", marginRight: "20px", marginLeft: "20px" }}
              >
                <RangeSlider
                  onChange={handleChangeVideo1Cut}
                  min={0}
                  max={59}
                  step={1}
                  value={[
                    modifications?.["sourceFootage1920x1080"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_1_1920x1080_59s.mp4"
                    )?.startCutTimecode,
                    modifications?.["sourceFootage1920x1080"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_1_1920x1080_59s.mp4"
                    )?.endCutTimecode,
                  ]}
                />
              </Box>
              <SmallTextInput
                value={
                  modifications?.["sourceFootage1920x1080"]?.layers?.find(
                    (layer) =>
                      layer.type === "video" &&
                      layer.value === "videoReplace_1_1920x1080_59s.mp4"
                  )?.startCutTimecode
                }
                onChange={(val) => {
                  if (+val > 59 || +val < 0 || isNaN(+val) || val.length > 2)
                    return;
                  handleModificationChange(
                    +val,
                    "sourceFootage800x1000",
                    "startCutTimecode",
                    "video",
                    "videoReplace_1_800x1000_59s.mp4"
                  );
                  handleModificationChange(
                    +val,
                    "sourceFootage1920x1080",
                    "startCutTimecode",
                    "video",
                    "videoReplace_1_1920x1080_59s.mp4"
                  );
                  handleModificationChange(
                    +val,
                    "sourceFootage1080x1920",
                    "startCutTimecode",
                    "video",
                    "videoReplace_1_1080x1920_59s.mp4"
                  );
                }}
                label={"START"}
                containerStyle={{
                  marginBottom: "11px",
                }}
              />
              <SmallTextInput
                value={
                  modifications?.["sourceFootage1920x1080"]?.layers?.find(
                    (layer) =>
                      layer.type === "video" &&
                      layer.value === "videoReplace_1_1920x1080_59s.mp4"
                  )?.endCutTimecode
                }
                onChange={(val) => {
                  if (+val > 59 || +val < 0 || isNaN(+val) || val.length > 2)
                    return;
                  handleModificationChange(
                    +val,
                    "sourceFootage800x1000",
                    "endCutTimecode",
                    "video",
                    "videoReplace_1_800x1000_59s.mp4"
                  );
                  handleModificationChange(
                    +val,
                    "sourceFootage1920x1080",
                    "endCutTimecode",
                    "video",
                    "videoReplace_1_1920x1080_59s.mp4"
                  );
                  handleModificationChange(
                    +val,
                    "sourceFootage1080x1920",
                    "endCutTimecode",
                    "video",
                    "videoReplace_1_1080x1920_59s.mp4"
                  );
                }}
                label={"END"}
                containerStyle={{
                  marginBottom: "11px",
                  marginLeft: "8px",
                }}
              />
            </Box>
          </Box>
        </Box>
        {isHaveMergeVideos && (
          <Box>
            <Typography
              sx={{
                fontWeight: "500",
                fontSize: "20px",
                color: lightBlackColor,
              }}
            >
              SRC2 (30-59s)
            </Typography>
            <Box
              sx={{
                width: "100%",
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: blackColor,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "20px",
                }}
              >
                <TextInput
                  label={"9x16 SRC2"}
                  placeholder={"*.mp4 link"}
                  inputStyle={{ width: "105px" }}
                  disabled={!formats["1080x1920"]}
                  value={
                    modifications?.["sourceFootage1080x1920"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_2_1080x1920_59s.mp4"
                    )?.newValue
                  }
                  onChange={(val) =>
                    handleModificationChange(
                      val,
                      "sourceFootage1080x1920",
                      "newValue",
                      "video",
                      "videoReplace_2_1080x1920_59s.mp4"
                    )
                  }
                />
                <TextInput
                  label={"16x9 SRC2"}
                  placeholder={"*.mp4 link"}
                  inputStyle={{ width: "105px" }}
                  disabled={!formats["1920x1080"]}
                  value={
                    modifications?.["sourceFootage1920x1080"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_2_1920x1080_59s.mp4"
                    )?.newValue
                  }
                  onChange={(val) =>
                    handleModificationChange(
                      val,
                      "sourceFootage1920x1080",
                      "newValue",
                      "video",
                      "videoReplace_2_1920x1080_59s.mp4"
                    )
                  }
                />
                <TextInput
                  label={"4x5 SRC2"}
                  placeholder={"*.mp4 link"}
                  inputStyle={{ width: "105px" }}
                  disabled={!formats["800x1000"]}
                  value={
                    modifications?.["sourceFootage800x1000"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_2_800x1000_59s.mp4"
                    )?.newValue
                  }
                  onChange={(val) =>
                    handleModificationChange(
                      val,
                      "sourceFootage800x1000",
                      "newValue",
                      "video",
                      "videoReplace_2_800x1000_59s.mp4"
                    )
                  }
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "20px",
                    color: lightBlackColor,
                    fontWeight: "500",
                    marginRight: "20px",
                  }}
                >
                  CUT
                </Typography>
                <Box
                  sx={{
                    width: "170px",
                    marginRight: "20px",
                    marginLeft: "20px",
                  }}
                >
                  <RangeSlider
                    onChange={handleChangeVideo2Cut}
                    min={0}
                    max={59}
                    step={1}
                    value={[
                      modifications?.["sourceFootage1920x1080"]?.layers?.find(
                        (layer) =>
                          layer.type === "video" &&
                          layer.value === "videoReplace_2_1920x1080_59s.mp4"
                      )?.startCutTimecode,
                      modifications?.["sourceFootage1920x1080"]?.layers?.find(
                        (layer) =>
                          layer.type === "video" &&
                          layer.value === "videoReplace_2_1920x1080_59s.mp4"
                      )?.endCutTimecode,
                    ]}
                  />
                </Box>
                <SmallTextInput
                  value={
                    modifications?.["sourceFootage1920x1080"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_2_1920x1080_59s.mp4"
                    )?.startCutTimecode
                  }
                  onChange={(val) => {
                    if (+val > 59 || +val < 0 || isNaN(+val) || val.length > 2)
                      return;
                    handleModificationChange(
                      +val,
                      "sourceFootage800x1000",
                      "startCutTimecode",
                      "video",
                      "videoReplace_2_800x1000_59s.mp4"
                    );
                    handleModificationChange(
                      +val,
                      "sourceFootage1920x1080",
                      "startCutTimecode",
                      "video",
                      "videoReplace_2_1920x1080_59s.mp4"
                    );
                    handleModificationChange(
                      +val,
                      "sourceFootage1080x1920",
                      "startCutTimecode",
                      "video",
                      "videoReplace_2_1080x1920_59s.mp4"
                    );
                  }}
                  label={"START"}
                  containerStyle={{
                    marginBottom: "11px",
                  }}
                />
                <SmallTextInput
                  value={
                    modifications?.["sourceFootage1920x1080"]?.layers?.find(
                      (layer) =>
                        layer.type === "video" &&
                        layer.value === "videoReplace_2_1920x1080_59s.mp4"
                    )?.endCutTimecode
                  }
                  onChange={(val) => {
                    if (+val > 59 || +val < 0 || isNaN(+val) || val.length > 2)
                      return;
                    handleModificationChange(
                      +val,
                      "sourceFootage800x1000",
                      "endCutTimecode",
                      "video",
                      "videoReplace_2_800x1000_59s.mp4"
                    );
                    handleModificationChange(
                      +val,
                      "sourceFootage1920x1080",
                      "endCutTimecode",
                      "video",
                      "videoReplace_2_1920x1080_59s.mp4"
                    );
                    handleModificationChange(
                      +val,
                      "sourceFootage1080x1920",
                      "endCutTimecode",
                      "video",
                      "videoReplace_2_1080x1920_59s.mp4"
                    );
                  }}
                  label={"END"}
                  containerStyle={{
                    marginBottom: "11px",
                    marginLeft: "8px",
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default memo(Formats);
